import {
  AfterViewInit,
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  WritableSignal,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { UserDialogComponent } from '@/modules/users/user-dialog/user-dialog.component';
import {
  ActionTrackingAttributes,
  NavigationTrackingAttributes,
  RoleAttributes,
  UserReturn,
} from '../../../../server/types';
import { MatPaginator } from '@angular/material/paginator';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Chart, registerables } from 'chart.js';
import { ConfirmDialogComponent } from '@/shared/components/confirm-dialog/confirm-dialog.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import {
  SnackbarService,
  AuthService,
  ScreenSizeService,
  UsersService,
  DataService,
} from '@/shared/services';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as am5 from '@amcharts/amcharts5';
import * as am5flow from '@amcharts/amcharts5/flow';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as _ from 'lodash-es';
import { Dictionary } from 'lodash';
import { isPlatformBrowser } from '@angular/common';
import { Papa } from 'ngx-papaparse';

export interface User extends UserReturn {}

@Component({
  selector: 'app-users',
  templateUrl: './users-content.html',
  styleUrls: ['./users-content.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: false,
})
export class UsersContentComponent implements OnInit, OnDestroy, AfterViewInit {
  mobileView: boolean = false;
  // user data source
  public dataSource = new MatTableDataSource<User>([]);
  @ViewChild(MatTable) table!: MatTable<User>;

  displayedColumns: string[] = [
    'id',
    'username',
    'first_name',
    'last_name',
    'email',
    'role',
    'createdAt',
    'updatedAt',
    'action',
  ];
  originalUsersData!: UserReturn[];
  userSearchValue: string = '';
  expandedElement!: User | null;
  userRoles!: WritableSignal<RoleAttributes[]>;
  selectedRoleId: number | null = null;
  generatedCode: string = '';
  subscription: Subscription = new Subscription();
  expandedUser!: UserReturn;
  filteredExpandedUser!: UserReturn;
  totalClicks: number = 0;
  totalScrolls: number = 0;
  totalMouseMoves: number = 0;
  totalKeyPresses: number = 0;
  pageFilterOptions: string[] = [];
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  readonly userInteractionsForm = new FormGroup({
    date_range: new FormControl('today'),
    pages: new FormControl<string[]>([]),
  });

  readonly dateRange = new FormGroup({
    start: new FormControl<Date | null>(null, [Validators.required]),
    end: new FormControl<Date | null>(null, [Validators.required]),
  });

  navigationSankeyChartRoot: am5.Root | null = null;
  interactionDoughnutChart: Chart | null = null;
  interactionStackedBarChart: Chart | null = null;

  dateFilterOptions = [
    { value: 'customrange', text: 'Custom Range' },
    { value: 'alltime', text: 'All Time' },
    { value: 'today', text: 'Today' },
    { value: 'yesterday', text: 'Yesterday' },
    { value: 'last7days', text: 'Last 7 Days' },
    { value: 'last30days', text: 'Last 30 Days' },
    { value: 'thisweek', text: 'This Week' },
    { value: 'lastweek', text: 'Last Week' },
    { value: 'thismonth', text: 'This Month' },
    { value: 'lastmonth', text: 'Last Month' },
    { value: 'thisyear', text: 'This Year' },
    { value: 'lastyear', text: 'Last Year' },
  ];

  constructor(
    private _liveAnnouncer: LiveAnnouncer,
    private usersService: UsersService,
    private snackbarService: SnackbarService,
    public dialog: MatDialog,
    private _router: Router,
    private screenSizeService: ScreenSizeService,
    private authService: AuthService,
    private clipboard: Clipboard,
    private dataService: DataService,
    private papa: Papa,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    Chart.register(...registerables);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.subscription.add(
      this.usersService.getUsers().subscribe((users) => {
        this.populateUsers(users);
      }),
    );
    this.subscription.add(
      this.usersService.getUser().subscribe({
        next: (userInfo) => {
          this.updateTable(userInfo);
          this.snackbarService.open(
            'User updated successfully',
            'feedback',
            'OK',
          );
        },
        error: (error) => {
          if (error.status === 401) {
            this._router.navigate([environment['login_url']]);
          }
        },
      }),
    );
    this.subscription.add(
      this.screenSizeService.screenView$.subscribe((view) => {
        this.mobileView = view === 'mobile';
      }),
    );

    this.authService.loadRoles();
    this.userRoles = this.authService.getRoles();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  populateUsers(users: UserReturn[]) {
    const userTableData: User[] = users.map((userInfo) =>
      this.generateRow(userInfo),
    );
    this.originalUsersData = JSON.parse(JSON.stringify(userTableData));
    this.dataSource.data = userTableData;
  }

  updateTable(userInfo: UserReturn) {
    const index = this.dataSource.data.findIndex(
      (user) => user.id === userInfo.id,
    );
    this.dataSource.data[index] = this.generateRow(userInfo);
    this.dataSource.data = this.dataSource.data;
  }

  generateRow(userInfo: UserReturn) {
    const createdAt = new Date(userInfo.createdAt as string).toLocaleString();
    const updatedAt = new Date(userInfo.updatedAt as string).toLocaleString();

    userInfo.createdAt = createdAt;
    userInfo.updatedAt = updatedAt;

    return { ...userInfo };
  }

  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort) {
    // This example uses English messages. If your application supports
    // multiple language, you would internationalize these strings.
    // Furthermore, you can customize the message to add additional
    // details about the values being sorted.
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  showUser(user: User) {
    this.dialog.open(UserDialogComponent, {
      width: '400px',
      data: { ...user },
    });
  }

  deleteUser(user: UserReturn) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { prompt: 'Are you sure to delete this user?' },
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.subscription.add(
            this.usersService.deleteUser(user.id).subscribe({
              next: (_) => {
                this.dataSource.data = this.dataSource.data.filter(
                  (u) => u.id !== user.id,
                );
              },
              error: (err) => {},
            }),
          );
        }
      }),
    );
  }

  copyRegistrationCode() {
    this.clipboard.copy(this.generatedCode);
    this.snackbarService.open('Copied registration code');
  }

  saveRegistrationCode() {
    if (this.selectedRoleId === null) return;

    const registrationCode = this.generateRegisterCode();
    this.authService
      .saveUserRegistrationCode({
        code: registrationCode,
        role_id: this.selectedRoleId,
      })
      .subscribe({
        next: (_resCode) => {
          this.generatedCode = registrationCode;
          this.selectedRoleId = null;
        },
        error: (error: HttpErrorResponse) => {
          if (error.message.includes('Registration code already exists')) {
            this.saveRegistrationCode();
          } else {
            this.snackbarService.open(
              'Something went wrong. Please try again.',
              'error',
            );
          }
        },
      });
  }

  handleUserSearch(searchValue: string) {
    this.userSearchValue = searchValue;

    if (!searchValue) {
      this.dataSource.data = this.originalUsersData;
    } else {
      this.dataSource.data = this.originalUsersData.filter((row) => {
        const value =
          `${row.id}${row.username}${row.first_name}${row.last_name}${row.email}`.toLowerCase();
        return value.includes(searchValue.toLowerCase());
      });
    }
  }

  handleUserClick(expandedElement: User | null, user: User) {
    if (expandedElement === user) {
      this.expandedElement = null;
    } else {
      this.expandedElement = user;
      this.getUserDetails(user.id);
    }
    this.userInteractionsForm.patchValue({ date_range: 'last7days' });
  }

  getUserDetails(userId: number) {
    this.usersService.getUserById(userId, { full: true }).subscribe((user) => {
      this.expandedUser = user;

      const interactionDataGroupedByPages = _.groupBy(user.action_data, 'page');
      this.pageFilterOptions = Object.keys(interactionDataGroupedByPages).sort(
        (a, b) => a.localeCompare(b),
      );

      this.generateInteractionCharts(userId);
    });
  }

  generateInteractionCharts(userId: number) {
    this.navigationSankeyChartRoot = null;
    this.filterDataByDateRange();

    if (!this.filteredExpandedUser.action_data?.length) return;

    let interactionGroupedByAction = _.groupBy(
      this.filteredExpandedUser.action_data,
      'action',
    );
    // Sort the keys
    interactionGroupedByAction = Object.keys(interactionGroupedByAction)
      .sort() // Sort keys alphabetically
      .reduce((acc: any, key) => {
        acc[key] = interactionGroupedByAction[key];
        return acc;
      }, {});

    this.totalClicks = _.sum(
      interactionGroupedByAction['click']?.map((c) => c.count),
    );
    this.totalScrolls = _.sum(
      interactionGroupedByAction['scroll']?.map((c) => c.count),
    );
    this.totalMouseMoves = _.sum(
      interactionGroupedByAction['mousemove']?.map((c) => c.count),
    );
    this.totalKeyPresses = _.sum(
      interactionGroupedByAction['keydown']?.map((c) => c.count),
    );
    const totalInteractionCount =
      this.totalClicks +
      this.totalKeyPresses +
      this.totalScrolls +
      this.totalMouseMoves;

    setTimeout(() => {
      const actionKeys = Object.keys(interactionGroupedByAction);
      this.interactionDoughnutChart && this.interactionDoughnutChart.destroy();
      this.interactionDoughnutChart = new Chart(
        `interactions-doughnut-chart-${userId}`,
        {
          type: 'doughnut',
          data: {
            labels: actionKeys,
            datasets: [
              {
                data: actionKeys.map((action) => {
                  const actionData = interactionGroupedByAction[action];

                  return _.sum(actionData?.map((a) => a.count));
                }),
                backgroundColor: this.getBackgroundColors(),
                borderColor: this.getBorderColors(),
                borderWidth: 1,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top', // Position of the legend
              },
              datalabels: {
                formatter: (value: number, _ctx) => {
                  return `${value} (${this.dataService.round(value / totalInteractionCount)}%)`;
                },
              },
            },
          },
        },
      );

      const interactionsGroupedByPage = _.groupBy(
        this.filteredExpandedUser.action_data,
        'page',
      );
      const pageKeys = Object.keys(interactionsGroupedByPage);

      this.interactionStackedBarChart &&
        this.interactionStackedBarChart.destroy();
      this.interactionStackedBarChart = new Chart(
        `interactions-stacked-bar-chart-${userId}`,
        {
          type: 'bar',
          data: {
            labels: pageKeys,
            datasets: actionKeys.map((action, index) => {
              const data: any[] = []; // number array allow null values

              Object.values(interactionsGroupedByPage).forEach((pageData) => {
                if (pageData.length) {
                  const groupedByAction = _.groupBy(pageData, 'action');
                  const actionData = groupedByAction[action];

                  data.push(
                    actionData ? _.sum(actionData.map((a) => a.count)) : null,
                  );
                }
              });

              return {
                label: action,
                data,
                backgroundColor: this.getBackgroundColors().at(index),
                borderColor: this.getBorderColors().at(index),
                borderWidth: 1,
              };
            }),
          },
          options: {
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top', // Position of the legend
              },
              tooltip: {
                enabled: true, // Enable tooltips
              },
            },
            scales: {
              x: {
                stacked: true, // Enable stacking on the x-axis
              },
              y: {
                stacked: true, // Enable stacking on the y-axis
              },
            },
          },
        },
      );

      this.interactionDoughnutChart.update();
      this.interactionStackedBarChart.update();
      this.generateNavigationArcDiagram(this.filteredExpandedUser);
    });
  }

  generateNavigationArcDiagram(userData: UserReturn) {
    this.navigationSankeyChartRoot &&
      this.navigationSankeyChartRoot.container.children.pop();

    if (!this.navigationSankeyChartRoot) {
      this.navigationSankeyChartRoot = am5.Root.new(
        `navigation-sankey-chart-${userData.id}`,
      );
    }
    this.navigationSankeyChartRoot._logo!.dispose();

    // Set theme
    this.navigationSankeyChartRoot.setThemes([
      am5themes_Animated.new(this.navigationSankeyChartRoot),
    ]);
    // Create series
    let series = this.navigationSankeyChartRoot.container.children.push(
      am5flow.ArcDiagram.new(this.navigationSankeyChartRoot, {
        width: am5.p100,
        height: am5.p100,
        sourceIdField: 'from',
        targetIdField: 'to',
        valueField: 'value',
        orientation: 'horizontal',
      }),
    );

    series.nodes.labels.template.setAll({
      fontSize: '1em',
      paddingLeft: 20,
      paddingRight: 20,
    });

    series.nodes.labels.template.adapters.add('x', function (x, target) {
      let dataItem = target.dataItem as any;
      if (dataItem) {
        if (dataItem.get('circle').get('radius') > 30) {
          target.set('centerX', am5.p50);
          target.set('rotation', 0);
        } else {
          target.set('centerX', 0);
          target.set('rotation', 90);
        }
      }
      return x;
    });

    // Sample data
    series.data.setAll(this.getArcDiagramData(userData.navigation_data!));

    let linkTooltip = am5.Tooltip.new(this.navigationSankeyChartRoot, {
      labelText: '{sourceId} → {targetId}\nValue: {value}',
    });

    series.links.template.set('tooltip', linkTooltip);
  }

  exportUserInteractionData() {
    if (isPlatformBrowser(this.platformId)) {
      const actionsData = this.filteredExpandedUser.action_data!.map(
        ({ action, page, count, createdAt }) => {
          return {
            action,
            page,
            count,
            createdAt: createdAt?.toString() || '',
          };
        },
      );

      const navigationData = this.filteredExpandedUser.navigation_data!.map(
        ({ from, to, createdAt }) => {
          return {
            from,
            to,
            createdAt: createdAt?.toString() || '',
          };
        },
      );
      const actionsCsv = this.papa.unparse(actionsData);
      const navigationCsv = this.papa.unparse(navigationData);

      // Optional: Add a label between sections
      const combinedCsv = `User's Actions\n${actionsCsv}\n\nUser's Navigations\n${navigationCsv}`;

      // Download as CSV file
      const blob = new Blob([combinedCsv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `${this.filteredExpandedUser.first_name}_${this.filteredExpandedUser.last_name}_interactions_${new Date().toLocaleDateString()}.csv`,
      );
      link.click();
    }
  }

  private getBackgroundColors() {
    return [
      'rgba(255, 99, 132, 0.6)', // Red
      'rgba(54, 162, 235, 0.6)', // Blue
      'rgba(255, 206, 86, 0.6)', // Yellow
      'rgba(75, 192, 192, 0.6)', // Green
    ];
  }

  private getBorderColors() {
    return [
      'rgba(255, 99, 132, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(75, 192, 192, 1)',
    ];
  }

  private generateRegisterCode() {
    const length = 16;
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
  }

  private filterDataByDateRange(): UserReturn {
    this.filteredExpandedUser = JSON.parse(
      JSON.stringify(this.expandedUser),
    ) as UserReturn;
    const now = new Date();
    let startDate: Date | null = null;
    let endDate: Date = new Date(); // Default to today

    const pages = this.userInteractionsForm.value.pages;

    if (pages?.length) {
      this.filteredExpandedUser.action_data =
        this.filteredExpandedUser.action_data?.filter((a) =>
          pages.includes(a.page),
        );
    }

    switch (this.userInteractionsForm.value.date_range) {
      case 'today':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        break;

      case 'yesterday':
        startDate = new Date(now.setDate(now.getDate() - 1));
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'last7days':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;

      case 'last30days':
        startDate = new Date(now.setDate(now.getDate() - 30));
        break;

      case 'thisweek':
        startDate = new Date(now.setDate(now.getDate() - now.getDay())); // Start of the week (Sunday)
        break;

      case 'lastweek':
        startDate = new Date(now.setDate(now.getDate() - now.getDay() - 7)); // Start of last week
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6); // End of last week
        break;

      case 'thismonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;

      case 'lastmonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
        break;

      case 'thisyear':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;

      case 'lastyear':
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31);
        break;

      case 'customrange':
        if (this.dateRange.value.start && this.dateRange.value.end) {
          startDate = new Date(this.dateRange.value.start);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(this.dateRange.value.end);
          endDate.setHours(23, 59, 59, 999);
        }
        break;

      default:
        return this.filteredExpandedUser; // If range is unknown, return all data
    }

    this.filteredExpandedUser.action_data =
      this.filteredExpandedUser.action_data!.filter((actionItem) => {
        const itemDate = new Date(actionItem.createdAt!);
        return startDate && endDate
          ? itemDate >= startDate && itemDate <= endDate
          : true;
      });

    this.filteredExpandedUser.navigation_data =
      this.filteredExpandedUser.navigation_data!.filter((actionItem) => {
        const itemDate = new Date(actionItem.createdAt!);
        return startDate && endDate
          ? itemDate >= startDate && itemDate <= endDate
          : true;
      });

    return this.filteredExpandedUser;
  }

  private getArcDiagramData(records: NavigationTrackingAttributes[]) {
    const nodeMap = new Map();
    const linksMap = new Map();
    let nodeIndex = 0;

    // Function to get or create node index
    function getNode(name: string) {
      if (!nodeMap.has(name)) {
        nodeMap.set(name, nodeIndex++);
      }
      return nodeMap.get(name);
    }

    // Process records to build nodes and links
    records.forEach(({ from, to }) => {
      const source = getNode(from);
      const target = getNode(to);
      const key = `${source}-${target}`;

      if (linksMap.has(key)) {
        linksMap.get(key).value += 1;
      } else {
        linksMap.set(key, { from: source, to: target, value: 1 });
      }
    });

    // Convert nodes and links into array format
    const nodes = Array.from(nodeMap.keys()).map((name) => ({ name }));
    const links = Array.from(linksMap.values());

    const data = links.map((link) => {
      return {
        from: nodes[link.from].name,
        to: nodes[link.to].name,
        value: link.value,
      };
    });

    return data;
  }
}
